import { useSortable } from '@dnd-kit/sortable';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UserProfileMandatoryFields } from 'api';
import { ReactComponent as EditIcon } from 'assets/icons/containerActions/edit.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/containerActions/trash.svg';
import { ReactComponent as EyeSlashIcon } from 'assets/icons/eyeSlashSolid.svg';
import { ReactComponent as EyeIcon } from 'assets/icons/eyeSolid.svg';
import { alert, NewConfirmDialog } from 'components';
import { useAuth, useModal } from 'hooks';

import { FieldContainer, FieldInput, IconSpan, LabelText } from './styled';

interface SortableFieldProps {
  id: string;
  label: string;
  hidden: boolean;
  dataCount: number;
  defaults?: boolean;
  onLabelChange: (id: string, newLabel: string) => void;
  onHideToggle: (id: string, hidden: boolean) => void;
  onDelete: (id: string) => void;
}

export default function SortableField({
  id,
  label,
  dataCount,
  defaults,
  hidden,
  onLabelChange,
  onHideToggle,
  onDelete,
}: SortableFieldProps) {
  const isNonMovable = ['email', 'name'].includes(label.toLowerCase());

  const { attributes, listeners, setNodeRef, transition, isDragging } = useSortable({
    id,
    disabled: isNonMovable,
  });
  const { t } = useTranslation();
  const { isManager } = useAuth();
  const { handleOpenModal, handleCloseModal, isOpen } = useModal();

  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(label);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditLabel(label);
  }, [label]);

  const handleEditClick = (e: { stopPropagation: () => void } | undefined) => {
    e?.stopPropagation();
    setIsEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleHideClick = (e: { stopPropagation: () => void } | undefined) => {
    e?.stopPropagation();
    onHideToggle(id, (hidden = !hidden));
  };

  const handleDeleteTarget = () => {
    onDelete(id);
    handleCloseModal();
  };
  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditLabel(e.target.value);
  };

  const handleLabelSave = () => {
    if (!editLabel) {
      alert.error(t('toasts.codes.fieldEmpty'));
      return;
    }
    onLabelChange(id, editLabel);
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleLabelSave();
    }
  };

  const style = {
    transition,
    zIndex: isDragging ? 100 : 'auto',
  };

  return (
    <FieldContainer
      ref={setNodeRef}
      transition={transition}
      custom={!defaults}
      style={style}
      {...(!isNonMovable ? { ...attributes } : {})}
    >
      <IconSpan
        {...(!isNonMovable ? { ...listeners } : {})}
        style={!isNonMovable ? { touchAction: 'none' } : { opacity: 0.3, cursor: 'default' }}
      >
        ☰
      </IconSpan>
      {isEditing ? (
        <FieldInput
          ref={inputRef}
          type="text"
          value={editLabel}
          onChange={handleLabelChange}
          onBlur={handleLabelSave}
          onKeyDown={handleKeyPress}
        />
      ) : (
        <LabelText onClick={defaults ? handleEditClick : undefined}>
          {defaults ? t(`settings.userProfile.fields.${label}`) : label}
        </LabelText>
      )}
      {isManager &&
        !UserProfileMandatoryFields.includes(label) &&
        (hidden ? (
          <EyeIcon onClick={handleHideClick} style={{ opacity: '0.4' }} />
        ) : (
          <EyeSlashIcon onClick={handleHideClick} style={{ opacity: '0.4' }} />
        ))}
      {isManager && !defaults && <EditIcon onClick={handleEditClick} style={{ opacity: '0.4' }} />}
      {isManager && !defaults && (
        <DeleteIcon onClick={handleOpenModal} style={{ opacity: '0.4' }} />
      )}
      {isOpen && (
        <NewConfirmDialog
          title={
            dataCount
              ? t('settings.userProfile.removeModal.dataExistTitle', {
                  fieldLabel: editLabel,
                  dataCount: dataCount,
                })
              : t('settings.userProfile.removeModal.title', {
                  fieldLabel: editLabel,
                })
          }
          subTitle={t('settings.userProfile.removeModal.subtitle')}
          open={isOpen}
          onConfirm={handleDeleteTarget}
          onClose={handleCloseModal}
          submitLabel={t('settings.userProfile.removeModal.successButton')}
          cancelLabel={t('settings.userProfile.removeModal.cancelButton')}
        />
      )}
    </FieldContainer>
  );
}
